import type { SWRConfiguration } from "swr";
import type {
  BaseRequestQueryParams,
  BaseResponse,
  ServiceResponseQuery,
} from "~/common/types/api.type";

import {
  useServiceFetcherResponse,
  useServiceRequest,
  type UseServiceFetcherOptions,
} from "~/common/hooks";
import { useFetcher } from "~/libs/swr";

/**
 * Internal shared hook to centralize request key/config creation and fetcher wiring.
 * Keeps hook call order stable and preserves the public APIs of `useQuery` and `useGet`.
 */
export interface QueryOptions<TResponse = unknown, TData = TResponse>
  extends UseServiceFetcherOptions<TResponse, TData> {
  enabled?: boolean;
  swrConfig?: SWRConfiguration;
}

/**
 * Shared options used by the query hooks.
 * Extracted to avoid duplicated inline typings across the hooks.
 */
const useBaseQuery = <
  TParams = unknown,
  TResponse = unknown,
  TData = TResponse
>(
  endpoint: string,
  request?: TParams,
  options?: QueryOptions<TResponse, TData>
): ServiceResponseQuery<TData> => {
  /** useServiceRequest (hook) */
  const { key, config } = useServiceRequest(endpoint, "GET", request!);

  /** Conditionally disable the request if not enabled */
  const requestKey = options?.enabled === false ? null : key;

  /** Request Config */
  const reqConfig = { ...config, ...options?.config };

  /** useServiceFetcherResponse (hook) */
  return useServiceFetcherResponse(
    useFetcher<BaseResponse<TResponse>>(
      requestKey,
      reqConfig,
      options?.swrConfig
    ),
    options
  );
};

/**
 * Generic hook for fetching list data
 * @template TResponse - The response type from the API
 * @template TData - The transformed data type (defaults to TResponse)
 * @template TRequest - The request parameters type (defaults to BaseRequestQueryParams)
 */
export const useQuery = <
  TRequest extends BaseRequestQueryParams = BaseRequestQueryParams,
  TResponse = unknown,
  TData = TResponse
>(
  endpoint: string,
  request?: TRequest,
  options?: QueryOptions<TResponse, TData>
): ServiceResponseQuery<TData> => {
  return useBaseQuery<TRequest, TResponse, TData>(endpoint, request, options);
};

/**
 * Generic hook for fetching single item data
 * @template TResponse - The response type from the API
 * @template TData - The transformed data type (defaults to TResponse)
 * @template TParams - The URL parameters or query params type
 */
export const useGet = <
  TResponse = unknown,
  TData = TResponse,
  TParams = unknown
>(
  endpoint: string,
  params?: TParams,
  options?: QueryOptions<TResponse, TData>
): ServiceResponseQuery<TData> => {
  /** Build URL with path parameters or use query parameters */
  const url =
    typeof params === "string" && params ? `${endpoint}/${params}` : endpoint;

  /** Query Params */
  const queryParams = typeof params === "object" ? params : undefined;

  /**
   * Resolve whether the request should be enabled. Extracted to a tiny helper
   * to avoid duplicating the "enabled" resolution logic in callers.
   */
  const resolveGetEnabled = (
    optEnabled: boolean | undefined,
    p: TParams | undefined
  ) => optEnabled !== false && (typeof p === "string" ? !!p : true);

  const enabled = resolveGetEnabled(options?.enabled, params);

  return useBaseQuery<TParams, TResponse, TData>(url, queryParams, {
    ...options,
    enabled,
  });
};
